import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import type { ReactNode } from 'react'

export type RadioOption = {
  value: string
  label: ReactNode
  description?: string
  /**
   * Optional content rendered immediately below this option (e.g. a
   * dropdown or input that's specific to this choice). Lets callers
   * interleave per-option controls inside the radio list.
   */
  content?: ReactNode
  /**
   * Disable this option so it can't be selected. Useful for shipping
   * methods that aren't available for the current cart (e.g. paints
   * can't be delivered).
   */
  disabled?: boolean
}

export type RadioGroupProps = {
  name: string
  legend: string
  value: string
  onChange: (v: string) => void
  options: RadioOption[]
}

export function RadioGroup({ name, legend, value, onChange, options }: RadioGroupProps) {
  return (
    <fieldset className="no-m al-radio-group__fieldset">
      <legend>{legend}</legend>
      <RadioGroupPrimitive.Root value={value} onValueChange={onChange} name={name}>
        {options.map((opt) => {
          const id = `${name}-${opt.value}`
          const labelId = `${id}-label`
          const descId = opt.description ? `${id}-desc` : undefined
          return (
            <div key={opt.value} data-disabled={opt.disabled ? '' : undefined}>
              <div className="al-radio-group__item">
                <RadioGroupPrimitive.Item
                  value={opt.value}
                  id={id}
                  className="input-radio"
                  aria-labelledby={labelId}
                  aria-describedby={descId}
                  disabled={opt.disabled}
                >
                  <RadioGroupPrimitive.Indicator />
                </RadioGroupPrimitive.Item>
                {/* Clicking the label still focuses + activates the Radix radio button. */}
                <label htmlFor={id} className="al-radio-group__label">
                  <span id={labelId} className="large">
                    {opt.label}
                  </span>
                  {opt.description ? <span id={descId}>{opt.description}</span> : null}
                </label>
              </div>
              {opt.content ? <div className="al-radio-group__content">{opt.content}</div> : null}
            </div>
          )
        })}
      </RadioGroupPrimitive.Root>
    </fieldset>
  )
}
